import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Layout } from '../../components/Layout/Layout'
import api from '../../api/axios'
import Icon from '../../design/icons'
import { fmtCurEUR } from '../../design/charts'
import { T, useContent } from '../../content/ContentProvider'

// ─── helpers ────────────────────────────────────────────────────────────────

const fmtDate = (s) => {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })
}

const fmtDateTime = (s) => {
  if (!s) return '—'
  return new Date(s).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
}

const periodLabel = (start, end) => {
  if (!start) return '—'
  return `${fmtDate(start)} → ${fmtDate(end)}`
}

const payLabels = (t) => ({
  BANK_TRANSFER: t('reports.detail.paymentType.bankTransfer'),
  BCC_CLEARING: t('reports.detail.paymentType.bccClearing'),
  CURRENT_ACCOUNT: t('reports.detail.paymentType.currentAccount'),
  CURRENT_ACCOUNT_CLEARING: t('reports.detail.paymentType.currentAccountClearing'),
})

const txLabels = (t) => ({
  BOOKING: t('reports.detail.txType.booking'),
  CANCELLATION: t('reports.detail.txType.cancellation'),
  EXCHANGE_BOOKING: t('reports.detail.txType.exchangeBooking'),
  EXCHANGE_CANCELLATION: t('reports.detail.txType.exchangeCancellation'),
  COMPENSATION: t('reports.detail.txType.compensation'),
  MANUAL_REFUND: t('reports.detail.txType.manualRefund'),
  PREBOOKING_TIME_EXTENSION: t('reports.detail.txType.prebookingTimeExtension'),
})

const recordNet = (rec) =>
  (rec.accountedAmounts || []).reduce((sum, a) => {
    const v = (a.amount || 0) / Math.pow(10, a.scale ?? 2)
    return sum + (a.settlementValueIndicator === 'CREDIT' ? v : -v)
  }, 0)

const recordCurrency = (rec) => rec.accountedAmounts?.[0]?.currency || 'EUR'

// ─── component ──────────────────────────────────────────────────────────────

export function ReportDetail() {
  const { t } = useContent()
  const PAY_LABEL = payLabels(t)
  const TX_LABEL = txLabels(t)
  const { reportId } = useParams()
  const navigate = useNavigate()

  const [g6, setG6] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedIdx, setSelectedIdx] = useState(null)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    setLoading(true)
    api
      .get(`/api/reporting/statementreports/${reportId}/g6`)
      .then((r) => { setG6(r.data); setLoading(false) })
      .catch((e) => {
        setError(e.response?.data?.message || e.response?.data?.error || t('reports.detail.error.load'))
        setLoading(false)
      })
  }, [reportId])

  const delivery = g6?.uicAccountingDelivery
  const details = delivery?.deliveryDetails || {}
  const records = useMemo(() => delivery?.accountingRecords || [], [delivery])
  const meta = g6?._ngrs || {}

  const balance = meta.balance || {}
  const balanceVal = balance.amount != null
    ? balance.amount / Math.pow(10, balance.scale ?? 2)
    : null

  const selectedRecord = selectedIdx != null ? records[selectedIdx] : null

  const downloadFile = async (format, ext) => {
    setDownloading(true)
    try {
      const res = await api.get(`/api/reporting/statementreports/${reportId}/export`, {
        params: { format },
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.setAttribute('download', `G6-${reportId}.${ext}`)
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      alert(t('reports.detail.error.download'))
    } finally {
      setDownloading(false)
    }
  }

  if (loading) {
    return (
      <Layout crumb={t('reports.detail.crumbLoading')}>
        <div className="view">
          <div className="view__body thin-scroll" style={{ color: 'var(--text-dim)', fontSize: 13 }}>
            <T k="reports.detail.loading" />
          </div>
        </div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout crumb={t('reports.detail.crumbError')}>
        <div className="view">
          <div className="view__body thin-scroll">
            <div className="status-msg status-msg--err" role="alert" aria-live="polite">
              {error}
            </div>
            <button className="btn" style={{ marginTop: 12 }} onClick={() => navigate('/reports')}>
              <T k="reports.detail.back" />
            </button>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout crumb={t('reports.detail.crumb', { reportId: meta.reportId || reportId })}>
      <div className="view">
        <div className="view__head">
          <div className="page-head">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button className="iconbtn" onClick={() => navigate('/reports')} style={{ width: 30, height: 30 }}>
                ←
              </button>
              <div>
                <h1 className="page-head__h" style={{ fontSize: 17 }}>{meta.reportId || reportId}</h1>
                <div className="page-head__sub" style={{ fontSize: 12 }}>
                  {meta.invoicingPartyName || details.invoicingParty || '—'}
                  {' → '}
                  {meta.invoiceRecipientName || details.invoiceRecipient || '—'}
                  {' · '}
                  {periodLabel(details.startStatementPeriod, details.endStatementPeriod)}
                  {' · '}
                  {PAY_LABEL[details.paymentType] || details.paymentType || '—'}
                </div>
              </div>
            </div>
            <div className="page-head__actions">
              <button className="btn" onClick={() => downloadFile('csv', 'csv')} disabled={downloading}>
                {Icon.download({ s: 12 })} <T k="reports.detail.action.csv" />
              </button>
              <button className="btn btn--primary" onClick={() => downloadFile('g6', 'json')} disabled={downloading}>
                {Icon.external({ s: 12 })} {downloading ? t('reports.detail.action.downloading') : t('reports.detail.action.downloadG6')}
              </button>
            </div>
          </div>
        </div>

        <div className="view__body thin-scroll">
        {/* KPIs */}
        <div className="grid-3" style={{ marginBottom: 18 }}>
          <div className="kpi kpi--accent">
            <div className="kpi__label"><T k="reports.detail.kpi.balance" /></div>
            <div className="kpi__value">{balanceVal != null ? fmtCurEUR(balanceVal) : '—'}</div>
            <div className="kpi__sub">{balance.currency || 'EUR'} · {PAY_LABEL[details.paymentType] || details.paymentType}</div>
          </div>
          <div className="kpi">
            <div className="kpi__label"><T k="reports.detail.kpi.accountingRecords" /></div>
            <div className="kpi__value">{records.length}</div>
            <div className="kpi__sub"><T k="reports.detail.kpi.transactionsSub" /></div>
          </div>
          <div className="kpi">
            <div className="kpi__label"><T k="reports.detail.kpi.deliveryUuid" /></div>
            <div className="kpi__value mono" style={{ fontSize: 10, lineHeight: 1.5, wordBreak: 'break-all' }}>
              {details.id || '—'}
            </div>
            <div className="kpi__sub">UIC accountingDelivery.id</div>
          </div>
        </div>

        {/* delivery details */}
        <div className="card" style={{ marginBottom: 16, padding: '14px 18px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', letterSpacing: '.05em',
            textTransform: 'uppercase', marginBottom: 10 }}>
            <T k="reports.detail.deliveryDetails" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px 24px', fontSize: 12 }}>
            <Kv label={t('reports.detail.field.invoicingParty')} value={`${details.invoicingParty || '—'} — ${meta.invoicingPartyName || ''}`} />
            <Kv label={t('reports.detail.field.invoiceRecipient')} value={`${details.invoiceRecipient || '—'} — ${meta.invoiceRecipientName || ''}`} />
            <Kv label={t('reports.detail.field.periodStart')} value={fmtDate(details.startStatementPeriod)} />
            <Kv label={t('reports.detail.field.periodEnd')} value={fmtDate(details.endStatementPeriod)} />
            <Kv label={t('reports.detail.field.paymentType')} value={PAY_LABEL[details.paymentType] || details.paymentType || '—'} />
            <Kv label={t('reports.detail.field.created')} value={fmtDateTime(details.creationDate)} />
          </div>
        </div>

        {/* records + detail panel */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

          <div className="card" style={{ flex: selectedRecord ? '0 0 58%' : '1', minWidth: 0 }}>
            <div style={{ padding: '12px 16px 0', fontSize: 11, fontWeight: 700, color: 'var(--text-dim)',
              letterSpacing: '.05em', textTransform: 'uppercase' }}>
              <T k="reports.detail.records.heading" vars={{ count: records.length }} />
            </div>

            {records.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>
                <T k="reports.detail.records.empty" />
              </div>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl table--cards">
                  <thead>
                    <tr>
                      <th style={{ width: 28 }}><T k="reports.detail.col.index" /></th>
                      <th><T k="reports.detail.col.type" /></th>
                      <th><T k="reports.detail.col.txType" /></th>
                      <th><T k="reports.detail.col.date" /></th>
                      <th className="num"><T k="reports.detail.col.amount" /></th>
                      <th style={{ width: 70 }}><T k="reports.detail.col.dc" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((rec, idx) => {
                      const net = recordNet(rec)
                      const cur = recordCurrency(rec)
                      const isCredit = net >= 0
                      const isSel = selectedIdx === idx
                      return (
                        <tr key={rec.id || idx}
                          onClick={() => setSelectedIdx(isSel ? null : idx)}
                          style={{ cursor: 'pointer',
                            background: isSel ? 'var(--bg-hover, rgba(64,120,245,.08))' : undefined }}>
                          <td className="muted mono" data-label={t('reports.detail.col.index')} style={{ fontSize: 11 }}>{idx + 1}</td>
                          <td data-label={t('reports.detail.col.type')}>
                            <span className="pill pill--neutral" style={{ fontSize: 10 }}>{rec.accountingType}</span>
                          </td>
                          <td data-label={t('reports.detail.col.txType')} style={{ fontSize: 12 }}>{TX_LABEL[rec.transaction?.type] || rec.transaction?.type || '—'}</td>
                          <td className="muted" data-label={t('reports.detail.col.date')} style={{ fontSize: 11 }}>{fmtDateTime(rec.transaction?.timestamp)}</td>
                          <td className="num bold" data-label={t('reports.detail.col.amount')} style={{ fontSize: 12,
                            color: isCredit ? 'var(--ok-dark)' : 'var(--err-dark)' }}>
                            {cur} {Math.abs(net).toFixed(2)}
                          </td>
                          <td data-label={t('reports.detail.col.dc')}>
                            <span className={`pill pill--${isCredit ? 'ok' : 'err'}`} style={{ fontSize: 10 }}>
                              <span className="pill-dot" />
                              {isCredit ? 'CREDIT' : 'DEBIT'}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {selectedRecord && (
            <div className="card" style={{ flex: '0 0 40%', minWidth: 0, fontSize: 12 }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)',
                  letterSpacing: '.05em', textTransform: 'uppercase' }}>
                  <T k="reports.detail.panel.record" vars={{ n: selectedIdx + 1 }} />
                </span>
                <button className="iconbtn" style={{ width: 24, height: 24 }}
                  onClick={() => setSelectedIdx(null)}>✕</button>
              </div>
              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>

                <Sect title={t('reports.detail.sect.record')}>
                  <Kv label={t('reports.detail.field.recordId')} value={selectedRecord.id} mono />
                  <Kv label={t('reports.detail.field.accountingType')} value={selectedRecord.accountingType} />
                  <Kv label={t('reports.detail.field.salesType')} value={selectedRecord.salesType} />
                  <Kv label={t('reports.detail.field.standard')} value={selectedRecord.transactionStandard || 'UIC'} />
                </Sect>

                <Sect title={t('reports.detail.sect.transaction')}>
                  <Kv label={t('reports.detail.field.type')} value={TX_LABEL[selectedRecord.transaction?.type] || selectedRecord.transaction?.type} />
                  <Kv label={t('reports.detail.field.timestamp')} value={fmtDateTime(selectedRecord.transaction?.timestamp)} />
                  <Kv label={t('reports.detail.field.contract')} value={selectedRecord.transaction?.contract} mono />
                </Sect>

                <Sect title={t('reports.detail.sect.subject')}>
                  <Kv label={t('reports.detail.field.items')} value={selectedRecord.subject?.numberOfItems ?? '1'} />
                  {selectedRecord.subject?.train && <Kv label={t('reports.detail.field.train')} value={selectedRecord.subject.train} />}
                  {selectedRecord.subject?.travelDate && <Kv label={t('reports.detail.field.travelDate')} value={selectedRecord.subject.travelDate} />}
                  {selectedRecord.subject?.carriers?.length > 0 && (
                    <Kv label={t('reports.detail.field.carriers')} value={selectedRecord.subject.carriers.join(', ')} />
                  )}
                  {selectedRecord.subject?.retailer && <Kv label={t('reports.detail.field.retailer')} value={selectedRecord.subject.retailer} />}
                </Sect>

                <Sect title={t('reports.detail.sect.accountedAmounts')}>
                  {(selectedRecord.accountedAmounts || []).map((a, i) => {
                    const flagLabel = a.grossAmount ? t('reports.detail.amount.gross') : a.afterSaleFee ? t('reports.detail.amount.afterSaleFee') : a.commission ? t('reports.detail.amount.commission') : '—'
                    const isCredit = a.settlementValueIndicator === 'CREDIT'
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '6px 0', borderBottom: i < (selectedRecord.accountedAmounts.length - 1) ? '1px solid var(--border)' : 'none' }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>{flagLabel}</div>
                          <div style={{ color: 'var(--text-dim)', fontSize: 11 }}><T k="reports.detail.amount.meta" vars={{ currency: a.currency || 'EUR', scale: a.scale ?? 2 }} /></div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 700, fontFamily: 'var(--font-mono)',
                            color: isCredit ? 'var(--ok-dark)' : 'var(--err-dark)' }}>
                            {(a.amount / Math.pow(10, a.scale ?? 2)).toFixed(2)} {a.currency || 'EUR'}
                          </div>
                          <span className={`pill pill--${isCredit ? 'ok' : 'err'}`} style={{ fontSize: 10 }}>
                            <span className="pill-dot" />
                            {a.settlementValueIndicator || 'CREDIT'}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </Sect>

              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </Layout>
  )
}

const Sect = ({ title, children }) => (
  <div>
    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dim)', letterSpacing: '.06em',
      textTransform: 'uppercase', marginBottom: 8 }}>
      {title}
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>{children}</div>
  </div>
)

const Kv = ({ label, value, mono }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
    <span style={{ color: 'var(--text-dim)', flexShrink: 0 }}>{label}</span>
    <span style={{ fontFamily: mono ? 'var(--font-mono)' : undefined, textAlign: 'right',
      color: 'var(--text)', wordBreak: 'break-all', fontSize: mono ? 11 : 12 }}>
      {value ?? '—'}
    </span>
  </div>
)
